import { AiChFeatureStatus, AiChattingService } from '@api'
import { combine, createEffect, sample } from 'effector'
import { useUnit } from 'effector-react'

import { $aiChSettings } from '@/models/ai-features/ai-chatting.ts'
import {
  $activeChatDetails,
  $chatFilter,
  $chatOrder,
  $chatQuery,
  $chatsMap,
  $chatsPages,
  $fanId,
  $selectedList,
  cancelGetChatsListRequest,
  getChatsDetailsFx,
  getChatsFx,
  getNextChatsPageFx,
  loadNextChatsPage,
  markChatAsReadFx,
  markChatAsUNReadFx,
  resetChats,
  scrollChatToBottomFx,
  togglePinMessage,
  updateLastMessage,
  updateLists,
} from '@/models/chattingUI/chats/chats.ts'
import { stopFanTyping } from '@/models/chattingUI/chats/typing.ts'
import { addSentMedia } from '@/models/chattingUI/media.ts'
import {
  $messages,
  $messagesByIdMap,
  addMessageByFanId,
  addOptimisticMessageByFanId,
  markAsReadById,
  markAsUNReadById,
  removeOptimisticMessageByFanId,
  resetMessagesByFanId,
  sendMessageFx,
} from '@/models/chattingUI/messages/messages.ts'
import {
  pinMessageFx,
  unpinMessageFx,
} from '@/models/chattingUI/messages/pinnedMessages.ts'
import { addReceivedMessage } from '@/models/chattingUI/unreadChatMessages.ts'
import { $creatorId, $currentCreator } from '@/models/creators.ts'
import { messageToMediaMessage } from '@/modules/chatting/state/sent-media-message-provider.tsx'
import { buildOptimisticMessage } from '@/modules/chatting/state/utils.ts'
import { matchesConditions } from '@/utils/chats.ts'

/**
 * Request a new chat list in case of change any of params.
 */
sample({
  clock: [$chatQuery, $chatFilter, $selectedList, $chatOrder, updateLists],
  fn: cancelGetChatsListRequest,
  target: resetChats,
})

resetChats.watch(() => {
  loadNextChatsPage()
})

/**
 * Add chat page.
 */
sample({
  clock: getNextChatsPageFx.doneData,
  source: { store: $chatsPages },
  fn: ({ store }, page) => store.concat(page),
  target: $chatsPages,
})

/**
 * Update messages in case if chats changed
 */
getChatsFx.doneData.watch(({ chats }) => {
  chats.forEach(({ withUser, lastMessage }) => {
    if (lastMessage) {
      addMessageByFanId({ fanId: withUser.id, message: lastMessage })
    }
  })
})

/**
 * Request chat details when we switch fan.
 */
sample({
  clock: $fanId,
  filter: (id) => !!id,
  fn: (id) => id!,
  target: getChatsDetailsFx,
})

/**
 * Request suggestions on chat switch
 */
sample({
  clock: [getChatsDetailsFx.doneData, $aiChSettings],
  source: {
    id: $creatorId,
    creator: $currentCreator,
    activeChat: $activeChatDetails,
    aiChSettings: $aiChSettings,
  },
  filter: (data) => {
    return (
      !!data.id &&
      !!data.creator?.onlyfansId &&
      !!data.activeChat &&
      data.aiChSettings?.featureStatus === AiChFeatureStatus.ENABLED
    )
  },
  fn: (data) => {
    return {
      creatorId: data.id!,
      ofCreatorId: data.creator!.onlyfansId,
      ofFanId: data.activeChat!.withUser.id,
    }
  },
  target: createEffect(AiChattingService.getAiChSuggestions),
})

/**
 * Always mark active chat as read.
 */
sample({
  clock: $activeChatDetails,
  source: {
    messagesMap: $messagesByIdMap,
  },
  filter: ({ messagesMap }, chat) =>
    !!chat?.lastMessageId &&
    !!messagesMap[chat.lastMessageId] &&
    !messagesMap[chat.lastMessageId].isReadByCreator,
  fn: (_, chat) => chat.lastMessageId!,
  target: markAsReadById,
})

sample({
  clock: markChatAsReadFx.done,
  source: {
    chatsMap: $chatsMap,
  },
  filter: ({ chatsMap }, { params: fanId }) => !!chatsMap[fanId]?.lastMessageId,
  fn: ({ chatsMap }, { params: fanId }) => chatsMap[fanId]!.lastMessageId!,
  target: markAsReadById,
})

sample({
  clock: markChatAsUNReadFx.done,
  source: {
    chatsMap: $chatsMap,
  },
  filter: ({ chatsMap }, { params: fanId }) => !!chatsMap[fanId]?.lastMessageId,
  fn: ({ chatsMap }, { params: fanId }) => chatsMap[fanId]!.lastMessageId!,
  target: markAsUNReadById,
})

/**
 * Update last chat message.
 */
sample({
  clock: updateLastMessage,
  filter: ({ lastMessage }) => !!lastMessage,
  fn: ({ lastMessage, fanId }) => ({ fanId, message: lastMessage! }),
  target: addMessageByFanId,
})

/**
 * Update last chat message.
 */
sample({
  clock: getChatsDetailsFx.done,
  filter: ({ result: { lastMessage } }) => !!lastMessage,
  fn: ({ result: { lastMessage }, params: fanId }) => ({
    fanId,
    message: lastMessage!,
  }),
  target: addMessageByFanId,
})

sample({
  clock: updateLastMessage,
  filter: ({ lastMessage: { isReadByCreator } }) => isReadByCreator,
  fn: ({ fanId, lastMessage: { id } }) => ({
    chatId: fanId,
    messageId: id,
  }),
  target: addReceivedMessage,
})

/**
 * Update send media.
 */
sample({
  clock: updateLastMessage,
  filter: ({ lastMessage: { media } }) => !!media?.length,
  fn: ({ fanId, lastMessage }) => [messageToMediaMessage(fanId, lastMessage)],
  target: addSentMedia,
})

/**
 * Mark user as stop typing.
 */
sample({
  clock: updateLastMessage,
  filter: ({ fanId, lastMessage }) => lastMessage.fromUser.id === fanId,
  fn: ({ fanId }) => fanId,
  target: stopFanTyping,
})

sample({
  clock: pinMessageFx.doneData,
  source: { fanId: $fanId },
  fn: ({ fanId }) => ({ fanId, pinned: true }),
  target: togglePinMessage,
})

sample({
  clock: unpinMessageFx.doneData,
  source: { fanId: $fanId },
  fn: ({ fanId }) => ({ fanId, pinned: false }),
  target: togglePinMessage,
})

sample({
  clock: addOptimisticMessageByFanId,
  fn: () => false,
  target: scrollChatToBottomFx,
})

export const useHasChatUNreadMessages = (chatId?: number) => {
  const chatsMap = useUnit($chatsMap)
  const fanId = useUnit($fanId)
  const messagesMap = useUnit($messagesByIdMap)

  if (!chatId || chatId === fanId) {
    return false
  }

  const lastMessageId = chatsMap[chatId]?.lastMessageId

  return (
    !!lastMessageId &&
    !!messagesMap[lastMessageId] &&
    !messagesMap[lastMessageId].isReadByCreator
  )
}

export const $lastActiveChatMessage = combine(
  $activeChatDetails,
  $messagesByIdMap,
  (activeChat, messagesMap) => {
    return activeChat?.lastMessageId
      ? (messagesMap[activeChat?.lastMessageId] ?? null)
      : null
  },
)

const $hasLastMessage = combine(
  $messages,
  $lastActiveChatMessage,
  (messages, lastMessage) => {
    return (
      messages.length > 0 &&
      !!lastMessage &&
      messages.some(({ id }) => id === lastMessage.id)
    )
  },
)

/**
 * Load the latest set of messages if we send a new message to the fan.
 */
sample({
  clock: sendMessageFx,
  source: {
    messages: $messages,
    lastMessage: $lastActiveChatMessage,
    fanId: $fanId,
  },
  filter: ({ messages, lastMessage, fanId }) =>
    !!fanId &&
    !!lastMessage &&
    !messages.some(({ id }) => id === lastMessage.id),
  fn: ({ fanId }) => ({ fanId: fanId! }),
  target: resetMessagesByFanId,
})

/**
 * Immediately add an optimistic message, if we targets to concrete chat.
 */
sample({
  clock: sendMessageFx,
  source: {
    creatorId: $creatorId,
  },
  filter: (_, { optimisticMessageId }) => !!optimisticMessageId,
  fn: ({ creatorId }, { userId, message, optimisticMessageId }) => {
    const optimisticMessage = buildOptimisticMessage({
      id: optimisticMessageId!,
      messageBody: message,
      creatorId: creatorId!,
    })

    return {
      fanId: userId,
      message: optimisticMessage,
    }
  },
  target: addOptimisticMessageByFanId,
})

/**
 * Remove an optimistic message when we got server response.
 */
sample({
  clock: sendMessageFx.done,
  filter: ({ params: { chat, optimisticMessageId, userId } }) =>
    !!chat && !!userId && !!optimisticMessageId,
  fn: ({ params: { userId, optimisticMessageId } }) => {
    return {
      fanId: userId,
      messageId: optimisticMessageId!,
    }
  },
  target: removeOptimisticMessageByFanId,
})

/**
 * Update last message data.
 */
sample({
  clock: sendMessageFx.done,
  source: {
    selectedList: $selectedList,
    query: $chatQuery,
    filter: $chatFilter,
    order: $chatOrder,
    fanId: $fanId,
  },
  filter: (_, { params: { chat }, result }) => !!chat && !!result,
  fn: (
    { filter, query, selectedList, order, fanId },
    { params: { userId, chat }, result },
  ) => {
    const messageFromUser = result!.fromUser.id === userId
    const markedAsRead = fanId === userId && messageFromUser
    // @ts-ignore
    const lastMessage: Message = {
      ...result!,
      isReadByCreator: result!.isReadByCreator || markedAsRead,
    }

    return {
      fanId: userId,
      lastMessage,
      pushToTheTopIfChanged:
        order === 'recent' &&
        !!matchesConditions(filter, query, selectedList, chat!) &&
        !lastMessage.isFromQueue,
    }
  },
  target: updateLastMessage,
})

/**
 * If we switch to unread chat - mark it as unread.
 */
sample({
  clock: $lastActiveChatMessage,
  source: { fanId: $fanId },
  filter: (_, message) => !!message && !message.isReadByCreator,
  fn: ({ fanId }) => fanId,
  target: markChatAsReadFx,
})
